import React, { useState } from 'react';
import { Box, Button, Checkbox, Divider, IconButton, ListItemText, Menu, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

import { COLUMNS, CrmContactTotal, describeRows, INITIAL_COLUMNS, normaliseColumns, PAGE_SIZES, PageSize } from './contactList';

interface ColumnChooserProps {
  shown: ReadonlyArray<string>;
  onChange: (columns: Array<string>) => void;
}

/**
 * Which columns the table shows. Tags, point person and email opens each cost
 * the API one more query per page, so they are asked only while shown.
 */
export const ColumnChooser = ({ shown, onChange }: ColumnChooserProps) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const toggle = (id: string) =>
    onChange(normaliseColumns(shown.includes(id) ? shown.filter((other) => other !== id) : [...shown, id]));

  return (
    <>
      <Button
        size="small"
        startIcon={<ViewColumnIcon />}
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
      >
        Columns
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {COLUMNS.map((column) => (
          <MenuItem
            key={column.id}
            dense
            disabled={column.required}
            onClick={() => toggle(column.id)}
            role="menuitemcheckbox"
            aria-checked={shown.includes(column.id)}
          >
            <Checkbox size="small" edge="start" checked={shown.includes(column.id)} tabIndex={-1} disableRipple />
            <ListItemText primary={column.label} />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem dense onClick={() => onChange(INITIAL_COLUMNS)}>
          <ListItemText primary="Reset to the usual columns" />
        </MenuItem>
      </Menu>
    </>
  );
};

interface ContactPagerProps {
  page: number;
  pageSize: PageSize;
  rowsOnPage: number;
  hasNextPage: boolean;
  /** Exact when the consent counts are in, or the API counted all (≤ 10,000). */
  total: CrmContactTotal | undefined;
  disabled: boolean;
  onPage: (page: number) => void;
  onPageSize: (pageSize: PageSize) => void;
}

/**
 * Paging driven by `hasNextPage`, not by the total: above 10,000 the total is
 * an estimate (§7). "Last page" is offered only once the total is exact.
 */
export const ContactPager = ({
  page,
  pageSize,
  rowsOnPage,
  hasNextPage,
  total,
  disabled,
  onPage,
  onPageSize,
}: ContactPagerProps) => {
  const lastPage = total?.exact ? Math.max(0, Math.ceil(total.count / pageSize) - 1) : undefined;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
      <TextField
        select
        size="small"
        label="Rows per page"
        value={pageSize}
        onChange={(event) => onPageSize(Number(event.target.value) as PageSize)}
        sx={{ minWidth: 120 }}
      >
        {PAGE_SIZES.map((size) => (
          <MenuItem key={size} value={size}>
            {size}
          </MenuItem>
        ))}
      </TextField>

      <Typography variant="body2" sx={{ mx: 1 }} aria-live="polite">
        Rows {describeRows(page, pageSize, rowsOnPage)}
        {lastPage !== undefined && ` · page ${(page + 1).toLocaleString('en-US')} of ${(lastPage + 1).toLocaleString('en-US')}`}
      </Typography>

      <Tooltip title="First page">
        <span>
          <IconButton aria-label="First page" disabled={disabled || page === 0} onClick={() => onPage(0)}>
            <FirstPageIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Previous page">
        <span>
          <IconButton aria-label="Previous page" disabled={disabled || page === 0} onClick={() => onPage(page - 1)}>
            <NavigateBeforeIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Next page">
        <span>
          <IconButton aria-label="Next page" disabled={disabled || !hasNextPage} onClick={() => onPage(page + 1)}>
            <NavigateNextIcon />
          </IconButton>
        </span>
      </Tooltip>
      {lastPage !== undefined && (
        <Tooltip title="Last page">
          <span>
            <IconButton
              aria-label="Last page"
              disabled={disabled || page >= lastPage}
              onClick={() => onPage(lastPage)}
            >
              <LastPageIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};
